import {useState, useEffect} from 'react';
import { useNavigate } from 'react-router-dom';
import getAdminFeedback from '../functions/API/getAdminFeedback.js';
import AdminSidebar from '../components/admin/AdminSidebar.js';
import { toast } from '../lib/toast.js';
import Loader from '../components/loader.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Star, MessageSquare, BarChart3 } from 'lucide-react';

function StarRating({ rating, size = 'sm' }) {
    const sizeClass = size === 'sm' ? 'h-3.5 w-3.5' : 'h-5 w-5';
    return (
        <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map(i => (
                <Star
                    key={i}
                    className={`${sizeClass} ${i <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`}
                />
            ))}
        </div>
    );
}

export default function Feedback() {
    const navigate = useNavigate();
    const [feedbackList, setFeedbackList] = useState([]);
    const [stats, setStats] = useState({ total_count: 0, average_rating: 0, breakdown: {} });
    const [loading, setLoading] = useState(true);
    const [isLoaded, setIsLoaded] = useState(false);

    async function GetAPIData() {
        const response = await getAdminFeedback();
        if (response.response.ok && response.data) {
            setFeedbackList(response.data.feedback || []);
            setStats(response.data.stats || { total_count: 0, average_rating: 0, breakdown: {} });
        } else {
            toast.error('Failed to load feedback data.');
        }
        setIsLoaded(true);
    }

    useEffect(() => {
        GetAPIData();
    }, []);

    function formatDate(dateStr) {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString();
    }

    const maxBreakdownCount = Math.max(...Object.values(stats.breakdown || {}), 1);

    return (
        <>
            <AdminSidebar>
                {loading ? (
                    <Loader style='table' setLoadingState={setLoading} isLoaded={isLoaded}/>
                ) : (
                    <div>
                        <div className="sm:flex sm:items-center mb-6">
                            <div className="sm:flex-auto">
                                <h1 className="text-2xl text-left leading-6 text-gray-900">Feedback</h1>
                                <p className="mt-2 text-sm text-gray-700">
                                    Tenant feedback on completed requests
                                </p>
                            </div>
                        </div>

                        {/* Stats row */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                            <Card>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-medium text-gray-500">Average Rating</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex items-center gap-3">
                                        <span className="text-3xl font-bold text-gray-900">{stats.average_rating}</span>
                                        <Star className="h-6 w-6 text-amber-400 fill-amber-400" />
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-medium text-gray-500">Total Responses</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex items-center gap-3">
                                        <span className="text-3xl font-bold text-gray-900">{stats.total_count}</span>
                                        <MessageSquare className="h-6 w-6 text-gray-400" />
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-medium text-gray-500">Rating Breakdown</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-1.5">
                                        {[5, 4, 3, 2, 1].map(rating => {
                                            const count = stats.breakdown?.[rating] || 0;
                                            const pct = maxBreakdownCount > 0 ? (count / maxBreakdownCount) * 100 : 0;
                                            return (
                                                <div key={rating} className="flex items-center gap-2 text-xs">
                                                    <span className="w-3 text-gray-500 font-medium">{rating}</span>
                                                    <Star className="h-3 w-3 text-amber-400 fill-amber-400" />
                                                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                                        <div
                                                            className="h-full bg-amber-400 rounded-full transition-all"
                                                            style={{ width: `${pct}%` }}
                                                        />
                                                    </div>
                                                    <span className="w-6 text-right text-gray-500">{count}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Feedback table */}
                        <div className="flow-root">
                            <div className="overflow-x-auto">
                                <div className="inline-block min-w-full py-2 align-middle">
                                    <table className="min-w-full divide-y divide-gray-300 border border-gray-200 rounded-lg">
                                        <thead>
                                            <tr>
                                                <th scope="col" className="py-2.5 px-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                                    Task Flow
                                                </th>
                                                <th scope="col" className="py-2.5 px-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                                    Tenant
                                                </th>
                                                <th scope="col" className="py-2.5 px-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                                    Rating
                                                </th>
                                                <th scope="col" className="py-2.5 px-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                                    Comment
                                                </th>
                                                <th scope="col" className="py-2.5 px-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                                    Date
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white">
                                            {feedbackList.map((item) => (
                                                <tr
                                                    key={item.id}
                                                    onClick={() => navigate(`/taskinstances/${item.task_instance_id}`)}
                                                    className="cursor-pointer hover:bg-gray-50"
                                                >
                                                    <td className="px-3 py-2.5 text-xs font-medium text-gray-900">
                                                        {item.task_flow_name}
                                                    </td>
                                                    <td className="px-3 py-2.5 text-xs text-gray-500">
                                                        {item.tenant_name}
                                                    </td>
                                                    <td className="px-3 py-2.5 text-xs">
                                                        <StarRating rating={item.rating} />
                                                    </td>
                                                    <td className="px-3 py-2.5 text-xs text-gray-500 max-w-xs truncate">
                                                        {item.comment || <span className="text-gray-300">-</span>}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-500">
                                                        {formatDate(item.created_at)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {feedbackList.length === 0 && (
                                        <div className="text-center py-12">
                                            <p className="text-sm text-gray-500">No feedback submitted yet.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </AdminSidebar>
        </>
    );
}
